/**
 * RealmCrossing — border-crossing cinematic that fires once the first time
 * a student unlocks a new realm. Old realm exits left, pixel border banner
 * sweeps across, new realm name + emblem enters from the right with
 * realm-specific biome backdrop and flavor text.
 *
 * Design reference: design_handoff_realm_cinematic (realm-cinematic.jsx +
 * realm-cinematic-data.jsx). RAF-driven timeline; all 11 beats match the spec.
 */
import { useEffect, useRef, useState, useCallback } from 'react'

// ─── Realm data ───────────────────────────────────────────────────────────────
interface RealmData {
  key: string; name: string; short: string
  accent: string; accentDk: string; accentLt: string
  sky: string; skyDk: string; emblem: string
  clearedLine?: string; welcome?: string
}

export const CROSSING_REALMS: Record<string, RealmData> = {
  geo: {
    key: 'GEO', name: 'GEOGRAPHY FRONTIER', short: 'GEOGRAPHY',
    accent: '#3aa83a', accentDk: '#1e6b1e', accentLt: '#7adb7a',
    sky: '#7cc8f8', skyDk: '#3f7fb8', emblem: 'compass',
    clearedLine: 'Every hill charted. Every harbor named.',
  },
  civ: {
    key: 'CIV', name: 'CIVICS CAPITOL', short: 'CIVICS',
    accent: '#8c4eb8', accentDk: '#5a2f80', accentLt: '#c89ee6',
    sky: '#3a2f55', skyDk: '#241c38', emblem: 'capitol',
    welcome: 'You mastered the map. Now master the rules that govern who lives where — councils, ballots, and constitutions await.',
  },
  his: {
    key: 'HIS', name: 'HISTORY REALM', short: 'HISTORY',
    accent: '#c08a2a', accentDk: '#7a5212', accentLt: '#e9c878',
    sky: '#4a3a22', skyDk: '#2e2414', emblem: 'hourglass',
    welcome: 'Every place has a past. Step back through the ages to see how the world you charted came to be.',
  },
  eco: {
    key: 'ECO', name: 'ECO EXCHANGE', short: 'ECONOMICS',
    accent: '#d44a2a', accentDk: '#8a2a14', accentLt: '#f0926e',
    sky: '#46241c', skyDk: '#2c1510', emblem: 'coin',
    welcome: 'Laws shape nations; trade shapes the world. Enter the markets where every choice carries a cost.',
  },
}

// The three first-unlock crossings this feature covers.
export const CROSSINGS = [
  { id: 'geo-civ', from: 'geo', to: 'civ' },
  { id: 'geo-his', from: 'geo', to: 'his' },
  { id: 'civ-eco', from: 'civ', to: 'eco' },
]

// ─── Easing helpers ───────────────────────────────────────────────────────────
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const seg     = (t: number, a: number, b: number) => clamp01((t - a) / (b - a))
const easeOut    = (t: number) => 1 - Math.pow(1 - t, 3)
const easeInBack = (t: number) => { const c = 1.7; return c * t * t * t }
const easeOutBack = (t: number) => {
  const c1 = 1.70158, c3 = c1 + 1
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
}

// ─── Timeline marks (ms at REF_D = 7000ms) ───────────────────────────────────
const REF_D = 7000
const M = {
  bgCross:  [2200, 4200],
  oldExit:  [2100, 3050],   // old name: translateX 0 → -135%
  stamp:    [800,  2050],   // cleared stamp pops
  banner:   [1900, 3600],   // pixel gate sweeps L→R
  eyebrow:  [3500, 4100],   // "NOW ENTERING" fades in
  newEnter: [3200, 4300],   // new name: translateX 130% → 0
  emblem:   [3700, 4500],   // emblem scales in
  rule:     [4400, 4900],   // accent underline grows
  flavor:   [4500, 5700],   // flavor text rises
  prompt:   [6000, REF_D],  // blink prompt
}

// ─── Pixel emblem (one per realm) ────────────────────────────────────────────
function RealmEmblem({ realm, size = 108 }: { realm: string; size?: number }) {
  const r = CROSSING_REALMS[realm]
  const p = { width: size, height: size, viewBox: '0 0 100 100' as const,
              shapeRendering: 'crispEdges' as const,
              style: { overflow: 'visible' as const } }

  if (r.emblem === 'compass') return (
    <svg {...p}>
      <circle cx="50" cy="50" r="40" fill={r.accentDk} stroke="#0c0e1a" strokeWidth="4"/>
      <circle cx="50" cy="50" r="40" fill="none" stroke={r.accentLt} strokeWidth="2"/>
      {[0,90,180,270].map((a, i) => (
        <rect key={i} x="48" y="6" width="4" height="8" fill={r.accentLt}
              transform={`rotate(${a} 50 50)`}/>
      ))}
      <polygon points="50,18 58,50 50,46 42,50" fill="#ff5a4a"/>
      <polygon points="50,82 42,50 50,54 58,50" fill="#f0f0f0"/>
      <circle cx="50" cy="50" r="5" fill="#0c0e1a"/>
      <text x="50" y="14" textAnchor="middle"
        style={{ font: '7px "Press Start 2P",monospace', fill: '#fff' }}>N</text>
    </svg>
  )

  if (r.emblem === 'capitol') return (
    <svg {...p}>
      <path d="M 30 44 A 20 20 0 0 1 70 44 Z" fill={r.accentLt}/>
      <path d="M 36 44 A 14 14 0 0 1 64 44 Z" fill={r.accentDk}/>
      <rect x="48" y="14" width="4" height="8" fill={r.accentLt}/>
      <circle cx="50" cy="13" r="3" fill="#ffd840"/>
      <rect x="24" y="44" width="52" height="6" fill={r.accent}/>
      <polygon points="50,30 70,44 30,44" fill={r.accentLt} opacity=".5"/>
      {[28,40,52,64].map((x, i) => (
        <g key={i}>
          <rect x={x} y="50" width="8" height="34" fill={r.accentLt}/>
          <rect x={x+2} y="50" width="2" height="34" fill={r.accentDk}/>
        </g>
      ))}
      <rect x="20" y="84" width="60" height="8" fill={r.accentLt}/>
      <rect x="20" y="90" width="60" height="4" fill={r.accentDk}/>
    </svg>
  )

  if (r.emblem === 'hourglass') return (
    <svg {...p}>
      <rect x="18" y="10" width="64" height="8" fill={r.accentLt}/>
      <rect x="18" y="82" width="64" height="8" fill={r.accentLt}/>
      <polygon points="26,18 74,18 54,50 46,50" fill={r.accentDk} stroke={r.accentLt} strokeWidth="2"/>
      <polygon points="46,50 54,50 74,82 26,82" fill={r.accentDk} stroke={r.accentLt} strokeWidth="2"/>
      <polygon points="38,26 62,26 52,44 48,44" fill="#ffd840"/>
      <polygon points="48,58 52,58 60,76 40,76" fill="#ffd840"/>
      <rect x="49" y="44" width="2" height="14" fill="#ffd840"/>
    </svg>
  )

  // coin (eco)
  return (
    <svg {...p}>
      <ellipse cx="50" cy="54" rx="36" ry="38" fill={r.accentDk}/>
      <circle cx="50" cy="50" r="38" fill={r.accent} stroke="#0c0e1a" strokeWidth="4"/>
      <circle cx="50" cy="50" r="38" fill="none" stroke={r.accentLt} strokeWidth="2"/>
      <circle cx="50" cy="50" r="28" fill="none" stroke={r.accentLt} strokeWidth="2" strokeDasharray="3 4"/>
      <text x="50" y="64" textAnchor="middle"
        style={{ font: 'bold 34px "Press Start 2P",monospace', fill: '#ffd840',
                 filter: 'drop-shadow(0 2px 0 #0c0e1a)' }}>$</text>
    </svg>
  )
}

// ─── Biome silhouette backdrop ────────────────────────────────────────────────
function RealmBiome({ realm }: { realm: string }) {
  const r = CROSSING_REALMS[realm]
  return (
    <svg width="100%" height="100%" viewBox="0 0 1280 720" shapeRendering="crispEdges"
         preserveAspectRatio="xMidYMid slice" style={{ position: 'absolute', inset: 0 }}>
      <defs>
        <linearGradient id={`sky-cr-${realm}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={r.sky}/>
          <stop offset="1" stopColor={r.skyDk}/>
        </linearGradient>
      </defs>
      <rect width="1280" height="720" fill={`url(#sky-cr-${realm})`}/>

      {/* Civics grid overlay */}
      {realm === 'civ' && (
        <g opacity=".22">
          {Array.from({ length: 22 }, (_, i) => (
            <line key={`v${i}`} x1={i*60} y1="0" x2={i*60} y2="720" stroke={r.accentLt} strokeWidth="1"/>
          ))}
          {Array.from({ length: 13 }, (_, i) => (
            <line key={`h${i}`} x1="0" y1={i*60} x2="1280" y2={i*60} stroke={r.accentLt} strokeWidth="1"/>
          ))}
        </g>
      )}

      {/* Stars / motes for night realms */}
      {realm !== 'geo' && Array.from({ length: 40 }, (_, i) => (
        <rect key={i} x={(i*53)%1280} y={(i*89)%300} width="3" height="3"
              fill={r.accentLt} opacity={(i%5)/10 + 0.2}/>
      ))}

      {/* Geography: sun + clouds */}
      {realm === 'geo' && (
        <>
          <circle cx="1050" cy="150" r="60" fill="#fff3b0" opacity=".8"/>
          {[[200,120],[260,150],[230,135]].map(([x,y], i) => (
            <ellipse key={i} cx={x} cy={y} rx="46" ry="22" fill="#fff" opacity=".85"/>
          ))}
        </>
      )}

      {/* Ground band */}
      <rect x="0" y="560" width="1280" height="160" fill={r.accentDk}/>

      {/* Realm-specific silhouettes */}
      {realm === 'geo' && [120,360,640,920,1180].map((x, i) => (
        <path key={i} d={`M ${x-160} 560 Q ${x} ${430-i*8} ${x+160} 560 Z`}
              fill={r.accent} opacity=".9"/>
      ))}
      {realm === 'civ' && [180,420,660,900,1140].map((x, i) => (
        <g key={i} opacity=".9">
          <rect x={x-26} y="360" width="52" height="200" fill={r.accent}/>
          <polygon points={`${x-34},360 ${x+34},360 ${x},316`} fill={r.accentLt}/>
          {[-16,0,16].map((dx, j) => (
            <rect key={j} x={x+dx-3} y="380" width="6" height="180" fill={r.accentDk}/>
          ))}
        </g>
      ))}
      {realm === 'his' && [200,560,940].map((x, i) => (
        <polygon key={i} points={`${x-200} 560 ${x} ${300+i*20} ${x+200} 560`}
                 fill={r.accent} opacity=".9"/>
      ))}
      {realm === 'eco' && [160,360,560,760,960,1160].map((x, i) => (
        <g key={i} opacity=".9">
          <rect x={x-50} y="430" width="100" height="130" fill={r.accent}/>
          <polygon points={`${x-64},430 ${x+64},430 ${x},388`} fill={r.accentLt}/>
          {[-30,-10,10,30].map((dx, j) => (
            <rect key={j} x={x+dx-7} y="430" width="14" height="40"
                  fill={j%2 ? r.accentDk : '#f0d088'}/>
          ))}
        </g>
      ))}
    </svg>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
interface Props {
  from: string       // realm key: geo | civ | his | eco
  to: string
  duration?: number  // ms, default 7000
  onDone: () => void
}

export default function RealmCrossing({ from, to, duration = 7000, onDone }: Props) {
  const F = CROSSING_REALMS[from] ?? CROSSING_REALMS.geo
  const T = CROSSING_REALMS[to]   ?? CROSSING_REALMS.civ

  const [t, setT] = useState(0)
  const startRef  = useRef<number | null>(null)
  const rafRef    = useRef<number>(0)
  const doneRef   = useRef(false)
  const scale     = duration / REF_D

  // Scale a mark pair to the actual duration
  const mk = (m: number[]) => [m[0] * scale, m[1] * scale] as [number, number]

  const handleSkip = useCallback(() => {
    if (t < 4300 * scale) return   // don't allow skip during first beats
    cancelAnimationFrame(rafRef.current)
    if (!doneRef.current) { doneRef.current = true; onDone() }
  }, [t, scale, onDone])

  useEffect(() => {
    cancelAnimationFrame(rafRef.current)
    startRef.current = null
    doneRef.current = false
    setT(0)

    const tick = (ts: number) => {
      if (startRef.current == null) startRef.current = ts
      const elapsed = ts - startRef.current
      setT(elapsed)
      if (elapsed < duration) {
        rafRef.current = requestAnimationFrame(tick)
      } else if (!doneRef.current) {
        doneRef.current = true
        onDone()
      }
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [from, to, duration, onDone])

  // ── Derived animation values ────────────────────────────────────────────────
  const bg      = easeOut(seg(t, ...mk(M.bgCross)))
  const oldX    = -135 * easeInBack(seg(t, ...mk(M.oldExit)))
  const oldOp   = 1 - seg(t, mk(M.oldExit)[0] + (mk(M.oldExit)[1] - mk(M.oldExit)[0]) * 0.3, mk(M.oldExit)[1])
  const stamp   = seg(t, ...mk(M.stamp))
  const bannerX = -260 + (1280 + 520) * easeOut(seg(t, ...mk(M.banner)))
  const eyeOp   = seg(t, ...mk(M.eyebrow))
  const newX    = 130 * (1 - easeOutBack(seg(t, ...mk(M.newEnter))))
  const newOp   = seg(t, mk(M.newEnter)[0], mk(M.newEnter)[0] + (mk(M.newEnter)[1] - mk(M.newEnter)[0]) * 0.6)
  const embS    = easeOutBack(seg(t, ...mk(M.emblem)))
  const ruleW   = easeOut(seg(t, ...mk(M.rule)))
  const flavOp  = seg(t, ...mk(M.flavor))
  const flavY   = 18 * (1 - easeOut(seg(t, ...mk(M.flavor))))
  const blink   = t >= mk(M.prompt)[0] && (Math.floor(t / 450) % 2 === 0)

  return (
    <div
      onClick={handleSkip}
      style={{
        position: 'fixed', inset: 0, zIndex: 90, overflow: 'hidden',
        background: '#0c0e1a', fontFamily: '"Press Start 2P", monospace',
        cursor: t >= 4300 * scale ? 'pointer' : 'default',
      }}
    >
      {/* Biome backdrops crossfade old → new */}
      <div style={{ position: 'absolute', inset: 0, opacity: 1 - bg }}>
        <RealmBiome realm={from}/>
      </div>
      <div style={{ position: 'absolute', inset: 0, opacity: bg }}>
        <RealmBiome realm={to}/>
      </div>

      {/* Cinematic chrome: vignette + scanlines */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at center, transparent 52%, rgba(0,0,0,.55) 100%)' }}/>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: .18,
        backgroundImage: 'repeating-linear-gradient(0deg, #000 0 2px, transparent 2px 4px)' }}/>

      {/* Letterbox bars */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 54, background: '#000' }}/>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 54, background: '#000' }}/>

      {/* ── OLD realm name + cleared stamp (exits left) ── */}
      <div style={{
        position: 'absolute', top: '34%', left: 0, right: 0, textAlign: 'center',
        transform: `translateX(${oldX}%)`, opacity: oldOp,
        pointerEvents: 'none',
      }}>
        <div style={{ fontSize: 13, letterSpacing: '2px', color: F.accentLt, marginBottom: 18 }}>
          REALM CLEARED
        </div>
        <div style={{ fontSize: 58, color: '#fff', letterSpacing: '2px',
          textShadow: `0 4px 0 ${F.accentDk}, 0 0 24px ${F.accent}` }}>
          {F.short}
        </div>
        {F.clearedLine && (
          <div style={{ fontSize: 10, color: F.accentLt, marginTop: 12, opacity: stamp }}>
            {F.clearedLine}
          </div>
        )}
        {/* Cleared stamp */}
        <div style={{
          display: 'inline-block', marginTop: 26, padding: '10px 18px',
          border: `4px solid ${F.accentLt}`, color: F.accentLt, fontSize: 15,
          letterSpacing: '2px', boxShadow: `0 0 0 4px #0c0e1a`,
          transform: `rotate(-8deg) scale(${Math.max(0, 0.6 + 0.4 * easeOutBack(stamp))})`,
          opacity: stamp,
        }}>
          ★ 5 / 5 ★
        </div>
      </div>

      {/* ── Border banner sweep L→R ── */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, width: 260, left: 0,
        transform: `translateX(${bannerX}px) skewX(-8deg)`,
        background: `linear-gradient(90deg, transparent, ${T.accentDk} 30%, ${T.accent} 50%, ${T.accentLt} 50%, ${T.accent} 70%, transparent)`,
        boxShadow: `0 0 60px ${T.accent}`,
        pointerEvents: 'none',
      }}>
        {/* Bunting pixels along the banner */}
        {Array.from({ length: 9 }, (_, i) => (
          <div key={i} style={{
            position: 'absolute', left: 118, top: `${8 + i * 11}%`,
            width: 24, height: 18,
            background: i % 2 ? T.accentLt : '#ffd840',
            clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
          }}/>
        ))}
      </div>

      {/* ── NEW realm name + emblem (enters from right) ── */}
      <div style={{
        position: 'absolute', top: '30%', left: 0, right: 0, textAlign: 'center',
        transform: `translateX(${newX}%)`, opacity: newOp,
        pointerEvents: 'none',
      }}>
        {/* "NOW ENTERING" eyebrow */}
        <div style={{ fontSize: 13, letterSpacing: '3px', color: T.accentLt,
          marginBottom: 8, opacity: eyeOp }}>
          ✦ NOW ENTERING ✦
        </div>

        {/* Emblem + name side by side */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 26 }}>
          <span style={{ display: 'inline-block', transform: `scale(${Math.max(0, embS)})` }}>
            <RealmEmblem realm={to} size={108}/>
          </span>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 64, color: '#fff', letterSpacing: '2px', lineHeight: 1,
              textShadow: `0 5px 0 ${T.accentDk}, 0 0 28px ${T.accent}` }}>
              {T.short}
            </div>
            {/* Accent underline rule grows in */}
            <div style={{ height: 8, marginTop: 14, background: T.accent,
              width: `${ruleW * 100}%`, boxShadow: `0 0 16px ${T.accent}` }}/>
          </div>
        </div>
      </div>

      {/* ── Flavor text ── */}
      <div style={{
        position: 'absolute', top: '66%', left: '14%', right: '14%',
        textAlign: 'center', opacity: flavOp, pointerEvents: 'none',
      }}>
        <p style={{ transform: `translateY(${flavY}px)`, color: '#e7ecff',
          fontSize: 14, lineHeight: 2.0, letterSpacing: '.5px',
          textShadow: '0 2px 0 #000' }}>
          {T.welcome}
        </p>
      </div>

      {/* ── "Press to continue" blink prompt ── */}
      <div style={{
        position: 'absolute', bottom: 74, left: 0, right: 0, textAlign: 'center',
        opacity: blink ? 1 : 0, transition: 'opacity .08s',
        color: '#fff', fontSize: 12, letterSpacing: '2px',
        pointerEvents: 'none',
      }}>
        ▶ PRESS TO CONTINUE
      </div>
    </div>
  )
}
